import React, { useState, useEffect, useContext, useRef } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { SidebarContext } from '../context/SidebarContext';
import AdminStreams from '../components/admin/AdminStreams';
import AdminUsers from '../components/admin/AdminUsers';
import AdminQuests from '../components/admin/AdminQuests';
import AdminTournaments from '../components/admin/AdminTournaments';
import AdminTasks from '../components/admin/AdminTasks';
import AdminNFTCollections from '../components/admin/AdminNFTCollections';
import AdminNFTs from '../components/admin/AdminNFTs';
import { toast } from 'react-hot-toast';

const Admin = () => {
  const { user, isConnected } = useContext(AuthContext);
  const { isOpen } = useContext(SidebarContext);
  const location = useLocation();
  const navigate = useNavigate();

  // Parse URL parameters
  const queryParams = new URLSearchParams(location.search);
  const tabParam = queryParams.get('tab');
  const editParam = queryParams.get('edit');

  const [activeTab, setActiveTab] = useState(tabParam || 'nft-collections');
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [nftToEdit, setNftToEdit] = useState(editParam || null);

  // Use ref to track if navigation has been performed
  const navigationPerformed = useRef(false);

  useEffect(() => {
    // Check for admin status from localStorage or AuthContext
    const checkAdminStatus = () => {
      // First check auth context
      if (user && user.isAdmin) {
        setIsAdmin(true);
        setLoading(false);
        return;
      }

      // If not found in context, check localStorage directly
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const localUser = JSON.parse(userStr);
          if (localUser && localUser.isAdmin) {
            setIsAdmin(true);
          } else {
            if (!navigationPerformed.current) {
              toast.error('Access denied. Admin privileges required.');
              navigationPerformed.current = true;
              navigate('/');
            }
          }
        } catch (err) {
          console.error("Error parsing user data", err);
          if (!navigationPerformed.current) {
            navigationPerformed.current = true;
            navigate('/admin-login');
          }
        }
      } else {
        // No user data in localStorage
        if (!navigationPerformed.current) {
          navigationPerformed.current = true;
          navigate('/admin-login');
        }
      }

      setLoading(false);
    };

    checkAdminStatus();
  }, [user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
      </div>
    );
  }

  // If not admin, redirect will happen in useEffect
  if (!isAdmin) {
    return null;
  }

  // If authenticated but not admin, component will redirect via useEffect

  return (
    <div className="flex flex-col min-h-screen bg-gray-900 text-white">
      <Header />
      <div className="flex flex-1">
        <Sidebar />
        <main className={`flex-1 p-4 md:p-6 transition-all duration-300 ${isOpen ? 'md:ml-64' : ''}`}>
          <div className="container mx-auto">
            <h1 className="text-3xl font-bold mb-6">Admin Panel</h1>

            {/* Tabs */}
            <div className="border-b border-gray-700 mb-6">
              <div className="flex space-x-4 overflow-x-auto pb-2">
                <button
                  onClick={() => setActiveTab('users')}
                  className={`py-2 px-4 font-medium border-b-2 ${
                    activeTab === 'users'
                      ? 'text-blue-500 border-blue-500'
                      : 'border-transparent hover:text-blue-400'
                  }`}
                >
                  Users
                </button>
                <button
                  onClick={() => setActiveTab('streams')}
                  className={`py-2 px-4 font-medium border-b-2 ${
                    activeTab === 'streams'
                      ? 'text-blue-500 border-blue-500'
                      : 'border-transparent hover:text-blue-400'
                  }`}
                >
                  BitSport Lives
                </button>
                <button
                  onClick={() => setActiveTab('quests')}
                  className={`py-2 px-4 font-medium border-b-2 ${
                    activeTab === 'quests'
                      ? 'text-purple-500 border-purple-500'
                      : 'border-transparent hover:text-purple-400'
                  }`}
                >
                  Quests
                </button>
                <button
                  onClick={() => setActiveTab('tournaments')}
                  className={`py-2 px-4 font-medium border-b-2 ${
                    activeTab === 'tournaments'
                      ? 'text-green-500 border-green-500'
                      : 'border-transparent hover:text-green-400'
                  }`}
                >
                  Tournaments
                </button>
                <button
                  onClick={() => setActiveTab('tasks')}
                  className={`py-2 px-4 font-medium border-b-2 ${
                    activeTab === 'tasks'
                      ? 'text-yellow-500 border-yellow-500'
                      : 'border-transparent hover:text-yellow-400'
                  }`}
                >
                  Tasks
                </button>
                <button
                  onClick={() => setActiveTab('nft-collections')}
                  className={`py-2 px-4 font-medium border-b-2 ${
                    activeTab === 'nft-collections'
                      ? 'text-indigo-500 border-indigo-500'
                      : 'border-transparent hover:text-indigo-400'
                  }`}
                >
                  NFT Collections
                </button>
                <button
                  onClick={() => setActiveTab('nfts')}
                  className={`py-2 px-4 font-medium border-b-2 ${
                    activeTab === 'nfts'
                      ? 'text-pink-500 border-pink-500'
                      : 'border-transparent hover:text-pink-400'
                  }`}
                >
                  NFTs
                </button>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'users' && <AdminUsers />}
            {activeTab === 'streams' && <AdminStreams />}
            {activeTab === 'quests' && <AdminQuests />}
            {activeTab === 'tournaments' && <AdminTournaments />}
            {activeTab === 'tasks' && <AdminTasks />}
            {activeTab === 'nft-collections' && <AdminNFTCollections />}
            {activeTab === 'nfts' && <AdminNFTs nftToEdit={nftToEdit} />}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Admin;
